import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import isEqual from 'lodash/isEqual';
import { combineLatest, distinctUntilChanged, map, merge, Observable, startWith, tap } from 'rxjs';
import { Time } from '../../types';

interface TimeForm {
  hour: FormControl<number>;
  minute: FormControl<number>;
}

// Can lead to bug in case of date format change, for now only 24h used though
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

@UntilDestroy()
@Component({
  selector: 'y42-time-selector',
  templateUrl: './time-selector.component.html',
  styleUrls: ['./time-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSelectorComponent implements OnInit {
  constructor() {
    merge(this.setFormValueOnInputChange$, this.resetFormToMinTimeOrMaxTimeIfNeeded$)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  @Input()
  @InputObservable()
  time: Time | null = null;
  time$!: Observable<Time | null>;

  @Input()
  @InputObservable()
  minTime: Time | null = null;
  minTime$!: Observable<Time | null>;

  @Input()
  @InputObservable()
  maxTime: Time | null = null;
  maxTime$!: Observable<Time | null>;

  @Output()
  timeChanged = new EventEmitter<Time>();

  private get dateNow() {
    return new Date();
  }

  readonly form = new FormGroup<TimeForm>({
    hour: new FormControl<number>(this.time?.hour ?? this.dateNow.getHours(), { nonNullable: true }),
    minute: new FormControl<number>(this.time?.minute ?? this.dateNow.getMinutes(), { nonNullable: true }),
  });

  private readonly hourOptions$ = combineLatest([this.minTime$, this.maxTime$]).pipe(
    map(([minTime, maxTime]) => {
      if (!minTime && !maxTime) {
        return HOURS;
      }
      let filteredHours = [...HOURS];
      if (minTime) {
        filteredHours = filteredHours.filter((m) => m >= minTime.hour);
      }
      if (maxTime) {
        filteredHours = filteredHours.filter((m) => m <= maxTime.hour);
      }

      return filteredHours;
    }),
  );

  private readonly minuteOptions$ = combineLatest([
    this.minTime$,
    this.maxTime$,
    this.hourControl.valueChanges.pipe(startWith(this.hourControl.value)),
  ]).pipe(
    distinctUntilChanged<[Time | null, Time | null, number]>(isEqual),
    map(([minTime, maxTime, hour]) => {
      if (!minTime && !maxTime) {
        return MINUTES;
      }

      let filteredMinutes = [...MINUTES];
      if (minTime && hour === minTime.hour) {
        filteredMinutes = filteredMinutes.filter((m) => m >= minTime.minute);
      }
      if (maxTime && hour === maxTime.hour) {
        filteredMinutes = filteredMinutes.filter((m) => m <= maxTime.minute);
      }
      return filteredMinutes;
    }),
  );

  readonly state = connectState(this, {
    hourOptions: this.hourOptions$,
    minuteOptions: this.minuteOptions$,
  });

  protected get hourControl() {
    return this.form.controls.hour;
  }

  protected get minuteControl() {
    return this.form.controls.minute;
  }

  private readonly resetFormToMinTimeOrMaxTimeIfNeeded$ = combineLatest([
    this.form.valueChanges.pipe(startWith(this.form.value)),
    this.minTime$,
    this.maxTime$,
  ]).pipe(
    tap(([{ hour, minute }, minTime, maxTime]) => {
      if (!!minTime && !!hour) {
        if (hour < minTime.hour) {
          this.hourControl.setValue(minTime.hour);
        }
        if (hour === minTime.hour && minute && minute < minTime.minute) {
          this.minuteControl.setValue(minTime.minute);
        }
      }

      if (!!maxTime && !!hour) {
        if (hour > maxTime.hour) {
          this.hourControl.setValue(maxTime.hour);
        }
        if (hour === maxTime.hour && minute && minute > maxTime.minute) {
          this.minuteControl.setValue(maxTime.minute);
        }
      }
    }),
  );

  private readonly setFormValueOnInputChange$ = this.time$.pipe(
    distinctUntilChanged(isEqual),
    tap((time) => {
      if (!time) {
        time = { hour: this.dateNow.getHours(), minute: this.dateNow.getMinutes() };
      }
      this.form.setValue(time);
    }),
  );

  private readonly emitOnFormValueChange$ = this.form.valueChanges.pipe(
    startWith(this.form.value),
    map(() => this.form.getRawValue()),
    distinctUntilChanged(isEqual),
    tap(() => {
      const time = this.form.getRawValue();
      this.timeChanged.emit({ hour: time.hour, minute: time.minute });
    }),
  );

  protected readonly trackByValue = trackByValue;

  // Parent component won't get emissions happend at constructor time
  ngOnInit(): void {
    this.emitOnFormValueChange$.pipe(untilDestroyed(this)).subscribe();
  }
}
